import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, FileSignature } from 'lucide-react';
import type { ReactNode } from 'react';
import type { OfferApprovalItem } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { formatRelative } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { OFFER_APPROVALS_KEY, offersApi, useOfferApprovals } from '../api/offers-api';
import { BandNote, money } from '../offer-format';
import styles from './Offers.module.css';

/**
 * OFR-02 — the offers waiting on the viewer, on the same Approvals page as requisitions so an
 * approver has one place to look. The figures and the band are shown to whoever the step is
 * assigned to (§L23 "APR (assigned)"). Approve is one click and the item leaves at once
 * (CLAUDE.md §5a rule 1); sending back needs a reason, written on the offer itself.
 */
export function OfferApprovalsList(): ReactNode {
  const queue = useOfferApprovals();
  const queryClient = useQueryClient();
  const showToast = useToasts((state) => state.show);

  async function approve(item: OfferApprovalItem): Promise<void> {
    const before = queryClient.getQueryData<readonly OfferApprovalItem[]>(OFFER_APPROVALS_KEY);
    queryClient.setQueryData<readonly OfferApprovalItem[]>(OFFER_APPROVALS_KEY, (items) => (items ?? []).filter((row) => row.stepId !== item.stepId));
    try {
      await offersApi.decide(item.stepId, { version: item.stepVersion, decision: 'approve' });
      showToast({ tone: 'success', message: `${item.reference} approved`, detail: `${item.candidateName} · ${item.stepName}` });
      await queryClient.invalidateQueries({ queryKey: OFFER_APPROVALS_KEY });
    } catch (cause) {
      if (before) queryClient.setQueryData(OFFER_APPROVALS_KEY, before);
      showToast({ tone: 'danger', message: `${item.reference} was not approved`, detail: errorMessage(cause) });
    }
  }

  if (queue.isPending) return <SkeletonLines count={2} />;
  const items = queue.data ?? [];
  if (items.length === 0) return <p className={styles.quiet}>No offers are waiting on you.</p>;
  return (
    <ul className={styles.steps}>
      {items.map((item) => (
        <li key={item.stepId} className={styles.step} data-current="true">
          <div className={styles.stepHead}>
            <Link to="/offers/$offerId" params={{ offerId: item.offerId }} className={styles.fileLink}>
              {item.reference}
            </Link>
            <span className={styles.stepName}>{item.candidateName}</span>
          </div>
          <span className={styles.meta}>
            {item.jobTitle} · {item.departmentName} · {item.stepName} · waiting {formatRelative(item.waitingSince)}
            {item.onBehalfOfName ? ` · covering for ${item.onBehalfOfName}` : ''}
          </span>
          <div className={styles.actions}>
            <span className={styles.gross}>{money(item.terms.grossMonthlyMinor, item.terms.currency)}</span>
            <BandNote position={item.terms.bandPosition} minMinor={item.terms.bandMinMinor} maxMinor={item.terms.bandMaxMinor} currency={item.terms.currency} isEnforced />
          </div>
          <div className={styles.actions}>
            <Button variant="solid" icon={Check} onClick={() => void approve(item)}>
              Approve
            </Button>
            <Link to="/offers/$offerId" params={{ offerId: item.offerId }} className={styles.fileLink}>
              <FileSignature size={14} aria-hidden /> Open the offer to send it back
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}
